import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { themeV2 } from '../styles/themeV2';
import ButtonV2 from '../components/ButtonV2';
import CardV2 from '../components/CardV2';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: ${themeV2.gradients.hero};
  color: ${themeV2.colors.slate[800]};
  font-family: ${themeV2.typography.fontFamily.sans.join(', ')};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 ${themeV2.spacing.lg};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    padding: 0 ${themeV2.spacing['2xl']};
  }
  
  @media (min-width: ${themeV2.breakpoints.lg}) {
    padding: 0 ${themeV2.spacing['3xl']};
  }
`;

// Hero Section
const HeroSection = styled.section`
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -6rem;
    right: -6rem;
    width: 18rem;
    height: 18rem;
    border-radius: 50%;
    background: #e0f2fe;
    filter: blur(3rem);
    z-index: -1;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -6rem;
    left: -4rem;
    width: 18rem;
    height: 18rem;
    border-radius: 50%;
    background: #e0e7ff;
    filter: blur(3rem);
    z-index: -1;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  gap: ${themeV2.spacing['4xl']};
  align-items: center;
  padding: ${themeV2.spacing['4xl']} 0;
  
  @media (min-width: ${themeV2.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
    padding: ${themeV2.spacing['6xl']} 0;
  }
`;

const HeroContent = styled.div``;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${themeV2.spacing.sm};
  background: ${themeV2.colors.slate[100]};
  padding: ${themeV2.spacing.sm} ${themeV2.spacing.lg};
  border-radius: ${themeV2.borderRadius.full};
  font-size: ${themeV2.typography.fontSize.sm};
  font-weight: ${themeV2.typography.fontWeight.medium};
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const HeroTitle = styled.h1`
  margin: ${themeV2.spacing.lg} 0;
  font-size: ${themeV2.typography.fontSize['4xl']};
  font-weight: ${themeV2.typography.fontWeight.bold};
  line-height: ${themeV2.typography.lineHeight.tight};
  letter-spacing: -0.025em;
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    font-size: ${themeV2.typography.fontSize['5xl']};
  }
  
  .highlight {
    color: ${themeV2.colors.slate[900]};
  }
`;

const HeroDescription = styled.p`
  margin: ${themeV2.spacing.lg} 0;
  font-size: ${themeV2.typography.fontSize.lg};
  color: ${themeV2.colors.slate[600]};
  line-height: ${themeV2.typography.lineHeight.normal};
`;

const HeroActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeV2.spacing.lg};
  margin: ${themeV2.spacing['2xl']} 0;
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    flex-direction: row;
  }
`;

const HeroFeatures = styled.div`
  display: flex;
  align-items: center;
  gap: ${themeV2.spacing['2xl']};
  margin-top: ${themeV2.spacing['3xl']};
  font-size: ${themeV2.typography.fontSize.sm};
  color: ${themeV2.colors.slate[600]};
  flex-wrap: wrap;
  
  .feature {
    display: flex;
    align-items: center;
    gap: ${themeV2.spacing.sm};
    
    svg {
      width: 1rem;
      height: 1rem;
    }
  }
`;

const HeroImage = styled.div`
  position: relative;
  
  img {
    width: 100%;
    border-radius: ${themeV2.borderRadius['3xl']};
    box-shadow: ${themeV2.shadows['2xl']};
  }
`;

// Services Section
const ServicesSection = styled.section`
  padding: ${themeV2.spacing['4xl']} 0;
  background: ${themeV2.colors.white};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    padding: ${themeV2.spacing['6xl']} 0;
  }
`;

const SectionHeader = styled.div`
  max-width: 32rem;
  margin-bottom: ${themeV2.spacing['4xl']};
`;

const SectionTitle = styled.h2`
  font-size: ${themeV2.typography.fontSize['3xl']};
  font-weight: ${themeV2.typography.fontWeight.bold};
  color: ${themeV2.colors.slate[900]};
  margin-bottom: ${themeV2.spacing.lg};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    font-size: ${themeV2.typography.fontSize['4xl']};
  }
`;

const SectionDescription = styled.p`
  font-size: ${themeV2.typography.fontSize.base};
  color: ${themeV2.colors.slate[600]};
  margin: 0;
`;

const ServicesGrid = styled.div`
  display: grid;
  gap: ${themeV2.spacing['2xl']};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: ${themeV2.breakpoints.lg}) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

// CTA Section
const CTASection = styled.section`
  padding: ${themeV2.spacing['4xl']} 0;
`;

const CTAContainer = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: ${themeV2.borderRadius['3xl']};
  background: ${themeV2.colors.slate[900]};
  color: ${themeV2.colors.white};
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    opacity: 0.2;
    background-image: ${themeV2.gradients.cta};
  }
`;

const CTAGrid = styled.div`
  position: relative;
  display: grid;
  gap: ${themeV2.spacing['3xl']};
  align-items: center;
  padding: ${themeV2.spacing['3xl']};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    padding: ${themeV2.spacing['4xl']};
  }
  
  @media (min-width: ${themeV2.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CTAContent = styled.div``;

const CTATitle = styled.h3`
  font-size: ${themeV2.typography.fontSize['2xl']};
  font-weight: ${themeV2.typography.fontWeight.semibold};
  margin-bottom: ${themeV2.spacing.sm};
  color: ${themeV2.colors.white};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    font-size: ${themeV2.typography.fontSize['3xl']};
  }
`;

const CTADescription = styled.p`
  color: ${themeV2.colors.slate[300]};
  margin: 0 0 ${themeV2.spacing['2xl']} 0;
`;

const CTAActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${themeV2.spacing.lg};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    flex-direction: row;
  }
`;

// Trust Section
const TrustSection = styled.section`
  padding: ${themeV2.spacing['4xl']} 0;
`;

const TrustText = styled.p`
  text-align: center;
  font-size: ${themeV2.typography.fontSize.sm};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${themeV2.colors.slate[500]};
  margin-bottom: ${themeV2.spacing['2xl']};
`;

const TrustGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${themeV2.spacing['2xl']};
  align-items: center;
  opacity: 0.8;
  justify-content: center;
`;

const TrustImage = styled.img`
  border-radius: ${themeV2.borderRadius.xl};
  width: 100%;
  height: 280px;
  object-fit: cover;
`;

// Solutions Section
const SolutionsSection = styled.section`
  padding: ${themeV2.spacing['4xl']} 0;
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    padding: ${themeV2.spacing['6xl']} 0;
  }
`;

const SolutionsGrid = styled.div`
  display: grid;
  gap: ${themeV2.spacing['4xl']};
  align-items: start;
  
  @media (min-width: ${themeV2.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }
`;

const SolutionsContent = styled.div``;

const SolutionsList = styled.ul`
  margin-top: ${themeV2.spacing['2xl']};
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${themeV2.spacing.lg};
  color: ${themeV2.colors.slate[700]};
`;

const SolutionsItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${themeV2.spacing.lg};
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    margin-top: 0.125rem;
    flex-shrink: 0;
  }
`;

const SolutionsCards = styled.div`
  display: grid;
  gap: ${themeV2.spacing['2xl']};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SolutionCard = styled.div`
  background: ${themeV2.colors.white};
  border: 1px solid ${themeV2.colors.slate[200]};
  border-radius: ${themeV2.borderRadius['2xl']};
  padding: ${themeV2.spacing['2xl']};
  
  h3 {
    font-size: ${themeV2.typography.fontSize.xl};
    font-weight: ${themeV2.typography.fontWeight.semibold};
    color: ${themeV2.colors.slate[900]};
    margin: 0 0 ${themeV2.spacing.sm} 0;
  }
  
  > p {
    color: ${themeV2.colors.slate[500]};
    font-size: ${themeV2.typography.fontSize.sm};
    margin: 0 0 ${themeV2.spacing.lg} 0;
  }
`;

const SolutionFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${themeV2.spacing.sm};
  
  li {
    display: flex;
    align-items: center;
    gap: ${themeV2.spacing.sm};
    font-size: ${themeV2.typography.fontSize.sm};
    color: ${themeV2.colors.slate[700]};
    
    &::before {
      content: '✓';
      width: 1rem;
      height: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
      color: ${themeV2.colors.slate[600]};
    }
  }
`;

// Contact Section
const ContactSection = styled.section`
  padding: ${themeV2.spacing['4xl']} 0;
  background: ${themeV2.colors.white};
  
  @media (min-width: ${themeV2.breakpoints.sm}) {
    padding: ${themeV2.spacing['6xl']} 0;
  }
`;

const ContactGrid = styled.div`
  display: grid;
  gap: ${themeV2.spacing['2xl']};
  
  @media (min-width: ${themeV2.breakpoints.md}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const ContactCard = styled.div`
  background: ${themeV2.colors.white};
  border: 1px solid ${themeV2.colors.slate[200]};
  border-radius: ${themeV2.borderRadius['2xl']};
  padding: ${themeV2.spacing['2xl']};
  
  h3 {
    font-weight: ${themeV2.typography.fontWeight.semibold};
    color: ${themeV2.colors.slate[900]};
    margin: 0 0 ${themeV2.spacing.sm} 0;
  }
  
  > p {
    color: ${themeV2.colors.slate[500]};
    font-size: ${themeV2.typography.fontSize.sm};
    margin: 0;
    line-height: ${themeV2.typography.lineHeight.normal};
  }
`;

const ContactIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  display: grid;
  place-items: center;
  border-radius: ${themeV2.borderRadius.xl};
  background: ${themeV2.colors.slate[100]};
  margin-bottom: ${themeV2.spacing.sm};
  
  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: ${themeV2.colors.slate[700]};
  }
`;

const ContactEmail = styled.p`
  margin-top: ${themeV2.spacing.sm};
  font-size: ${themeV2.typography.fontSize.sm};
  color: ${themeV2.colors.slate[900]} !important;
`;

const ContactLocation = styled.p`
  margin-top: ${themeV2.spacing.sm};
  font-size: ${themeV2.typography.fontSize.sm};
  color: ${themeV2.colors.slate[900]} !important;
  display: inline-flex;
  align-items: center;
  gap: ${themeV2.spacing.sm};
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const HomeV2 = () => {
  const { t } = useTranslation(['pages', 'common']);

  return (
    <PageContainer>
      {/* Hero Section */}
      <HeroSection>
        <Container>
          <HeroGrid>
            <HeroContent>
              
              <HeroTitle>
                {t('pages:home.title')}
              </HeroTitle>
              
              <HeroDescription>
                {t('pages:home.subtitle')}
              </HeroDescription>
              
              <HeroActions>
                <Link to="/contacto">
                  <ButtonV2 variant="primary" size="large">
                    {t('pages:home.cta')}
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </ButtonV2>
                </Link>
              </HeroActions>
              
              <HeroFeatures>
                <div className="feature">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('pages:home.features.agile')}
                </div>
                <div className="feature">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  {t('pages:home.features.timeToValue')}
                </div>
                <div className="feature">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  {t('pages:home.features.security')}
                </div>
              </HeroFeatures>
            </HeroContent>
            
            <HeroImage>
              <img 
                src="https://images.unsplash.com/photo-1523961131990-5ea7c61b2107?q=80&w=1600&auto=format&fit=crop" 
                alt="Equipo tecnológico colaborando" 
              />
            </HeroImage>
          </HeroGrid>
        </Container>
      </HeroSection>

      {/* Trust Section */}
      <TrustSection>
        <Container>
          <TrustText>Confían en nosotros:</TrustText>
          <TrustGrid>
            <TrustImage src="https://upload.wikimedia.org/wikipedia/commons/2/25/LogoUNED.jpg" alt="UNED" />
            <TrustImage src="https://www.uam.es/images/logo-uam.png" alt="UAM" />
            <TrustImage src="https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR-TpAQIF4rVAr5Gb9r5Dm7tro4j4rBn-3GQg0OsuAlvFOz60JllCyiogYxfAUaajRqbV8&usqp=CAU" alt="Ministerio de universidades" />
          </TrustGrid>
        </Container>
      </TrustSection>

      {/* Services Section */}
      <ServicesSection>
        <Container>
          <SectionHeader>
            <SectionTitle>{t('pages:home.services.title')}</SectionTitle>
            <SectionDescription>
              {t('pages:home.services.subtitle')}
            </SectionDescription>
          </SectionHeader>
          
          <ServicesGrid>
            <CardV2
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                </svg>
              }
              title={t('pages:home.services.development.title')}
              description={t('pages:home.services.development.description')}
            />
            
            <CardV2
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              }
              title={t('pages:home.services.cloud.title')}
              description={t('pages:home.services.cloud.description')}
            />
            
            <CardV2
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              }
              title={t('pages:home.services.ai.title')}
              description={t('pages:home.services.ai.description')}
            />
            
            <CardV2
              icon={
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              }
              title={t('pages:home.services.security.title')}
              description={t('pages:home.services.security.description')}
            />
          </ServicesGrid>
        </Container>
      </ServicesSection>

      {/* Solutions Section */}
      <SolutionsSection>
        <Container>
          <SolutionsGrid>
            <SolutionsContent>
              <SectionTitle>Soluciones listas para despegar</SectionTitle>
              <SectionDescription>
                Aceleradores y arquitecturas probadas para bajar el riesgo y el time‑to‑market.
              </SectionDescription>
              <SolutionsList>
                <SolutionsItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                  UX research & design system
                </SolutionsItem>
                <SolutionsItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                  Arquitecturas cloud well‑architected
                </SolutionsItem>
                <SolutionsItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                  Observabilidad full‑stack
                </SolutionsItem>
                <SolutionsItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                  Gobernanza de datos
                </SolutionsItem>
                <SolutionsItem>
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4" />
                  </svg>
                  Agile
                </SolutionsItem>
              </SolutionsList>
            </SolutionsContent>
            
            <SolutionsCards>
              <SolutionCard>
                <h3>PoC - Pruebas de concepto</h3>
                <SolutionFeatures>
                  <li>Análisis y requerimientos</li>
                  <li>Puesta en marcha</li>
                  <li>Puesta en producción</li>
                </SolutionFeatures>
              </SolutionCard>
              
              <SolutionCard>
                <h3>Plataformas B2B</h3>
                <SolutionFeatures>
                  <li>Portales clientes/proveedores</li>
                  <li>SSO y auditoría</li>
                  <li>Integraciones ERP/CRM</li>
                </SolutionFeatures>
              </SolutionCard>
              
              <SolutionCard>
                <h3>Comunicaciones & VoIP</h3>
                <SolutionFeatures>
                  <li>Asterisk/Kamailio</li>
                  <li>Bots voz en tiempo real</li>
                  <li>Grabación y analítica</li>
                </SolutionFeatures>
              </SolutionCard>
              
              <SolutionCard>
                <h3>Data & Analytics</h3>
                <SolutionFeatures>
                  <li>ETL/ELT modernos</li>
                  <li>Dashboards</li>
                  <li>Modelos predictivos</li>
                </SolutionFeatures>
              </SolutionCard>
            </SolutionsCards>
          </SolutionsGrid>
        </Container>
      </SolutionsSection>

      {/* CTA Section */}
      <CTASection>
        <Container>
          <CTAContainer>
            <CTAGrid>
              <CTAContent>
                <CTATitle>{t('pages:home.ctaSection.title')}</CTATitle>
                <CTADescription>
                  {t('pages:home.ctaSection.subtitle')}
                </CTADescription>
                <CTAActions>
                  <Link to="/contacto">
                    <ButtonV2 variant="white" size="large">
                      {t('pages:home.ctaSection.contact')}
                    </ButtonV2>
                  </Link>
                </CTAActions>
              </CTAContent>
            </CTAGrid>
          </CTAContainer>
        </Container>
      </CTASection>

      {/* Contact Section */}
      <ContactSection>
        <Container>
          <ContactGrid>
            <ContactCard>
              <ContactIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
              </ContactIcon>
              <h3>Llámanos</h3>
              <p>Atención de 9:00 a 18:00 CET</p>
              <p>+34 601 440 746</p>
              <a href="https://wa.me/34601440746" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#25D366', textDecoration: 'none', marginTop: '8px', fontSize: '14px' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.051 3.488"/>
                </svg>
                WhatsApp
              </a>
            </ContactCard>
            
            <ContactCard>
              <ContactIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </ContactIcon>
              <h3>Escríbenos</h3>
              <p>Tiempo medio de respuesta: 1 día</p>
              <ContactEmail>info@amalgamatech.com</ContactEmail>
            </ContactCard>
            
            <ContactCard>
              <ContactIcon>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </ContactIcon>
              <h3>Ubicación</h3>
              <p>Cink Coworking Manoteras<br/>Av. de Manoteras, 24, Hortaleza, 28050 Madrid</p>
              <ContactLocation>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <a href="https://www.google.com/maps/search/Cink+Coworking+Manoteras+Av.+de+Manoteras+24+Hortaleza+28050+Madrid" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
                  Madrid
                </a>
              </ContactLocation>
            </ContactCard>
          </ContactGrid>
        </Container>
      </ContactSection>
    </PageContainer>
  );
};

export default HomeV2;